import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, X, ChevronRight, Package, Smartphone, Hash, Barcode, Tag, AlertCircle, ChevronDown, ChevronUp, Globe, Download, Upload, List, SlidersHorizontal, ScanBarcode, Save } from 'lucide-react';
import { usePIM } from '../context/PIMContext';
import { PrimaryButton } from './ui/PrimaryButton';
import { ExportModal } from './modals/ExportModal';
import { ImportModal } from './modals/ImportModal';
import { BASE_UPLOAD_URL } from '../config';

export const ProductListView = ({ onClose }) => {
    const { fetchProduct, saveProduct, categories, statuses, availableProperties, availableLanguages } = usePIM();
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [modifiedStatuses, setModifiedStatuses] = useState({}); // { [productId]: newStatus }
    const [isSaving, setIsSaving] = useState(false);
    const [filters, setFilters] = useState({
        q: '',
        sku: '',
        model: '',
        ean: '',
        category: '',
        status: '',
        color: '',
        trace_id: '', // IMEI or Serial
        property_name: '',
        property_value: '',
        language_id: '',
        language_id_2: ''
    });

    // Debounce search
    const performSearch = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.q) params.append('q', filters.q);
            if (filters.sku) params.append('sku', filters.sku);
            if (filters.model) params.append('model', filters.model);
            if (filters.ean) params.append('ean', filters.ean);
            if (filters.category) params.append('category', filters.category);
            if (filters.status) params.append('status', filters.status);
            if (filters.color) params.append('color', filters.color);
            if (filters.trace_id) params.append('trace_id', filters.trace_id);
            if (filters.property_name) params.append('property_name', filters.property_name);
            if (filters.property_value) params.append('property_value', filters.property_value);
            if (filters.language_id) params.append('language_id', filters.language_id);
            if (filters.language_id_2) params.append('language_id_2', filters.language_id_2);

            const res = await fetch(`/api/products/advanced-search?${params.toString()}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (res.ok) {
                const data = await res.json();
                setResults(data);
                // Reset selection when search results change? Maybe keep if ID still exists.
                // For now, let's keep selection but filter out invalid ones if needed, or just leave as is.
            }
        } catch (err) {
            console.error("Search failed:", err);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        const timer = setTimeout(() => {
            performSearch();
        }, 500);
        return () => clearTimeout(timer);
    }, [performSearch]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleProductSelect = useCallback(async (sku) => {
        await fetchProduct(sku);
        onClose();
    }, [fetchProduct, onClose]);

    const toggleSelectAll = () => {
        if (selectedProducts.length === results.length && results.length > 0) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(results.map(p => p.id));
        }
    };

    const toggleSelectProduct = useCallback((id) => {
        setSelectedProducts(prev => {
            if (prev.includes(id)) {
                return prev.filter(pid => pid !== id);
            } else {
                return [...prev, id];
            }
        });
    }, []);

    const handleImport = (importedData) => {
        let updatedCount = 0;
        let createdCount = 0;
        const newResults = [...results];

        importedData.forEach(item => {
            const existingIndex = newResults.findIndex(r => r.sku === item.sku);
            if (existingIndex >= 0) {
                // Update
                newResults[existingIndex] = { ...newResults[existingIndex], ...item };
                updatedCount++;
            } else {
                // Create (Simulated - adding to list)
                newResults.push({
                    id: Date.now() + Math.random(), // Temp ID
                    ...item,
                    status: item.status || 'Development', // Default
                    image_url: item.image_url || null
                });
                createdCount++;
            }
        });

        setResults(newResults);
        return `${updatedCount} products updated, ${createdCount} new products created.`;
    };

    const handleStatusChange = (productId, newStatus) => {
        setModifiedStatuses(prev => ({
            ...prev,
            [productId]: newStatus
        }));
    };

    const handleBulkSave = async () => {
        setIsSaving(true);
        const updates = Object.entries(modifiedStatuses);
        let successCount = 0;

        // Iterate and save each product
        // Note: For large datasets, a dedicated bulk update endpoint would be better,
        // but reusing saveProduct ensures all logic (like logs/history) is preserved.
        for (const [productId, status] of updates) {
            const product = results.find(p => p.id === parseInt(productId));
            if (product) {
                // Determine 'sku' if product object only has minimal data, but results usually has full scan.
                // We construct a payload that only updates what's needed, but saveProduct expects a full object or handling.
                // Looking at PIMContext.saveProduct, it handles updates if ID is present.
                // We need to pass the FULL current object with the NEW status.

                const payload = {
                    ...product,
                    status: status
                };

                const success = await saveProduct(payload);
                if (success) successCount++;
            }
        }

        // Reload to refresh state and clear modified
        setIsSaving(false);
        setModifiedStatuses({});
        performSearch(); // Refresh list
        // Ideally show a toast here, but for now we rely on the UI update
    };

    return (
        <div className="fixed inset-0 z-50 bg-stone-950 flex flex-col animate-in fade-in duration-200">
            {/* Modals */}
            <ExportModal
                isOpen={isExportOpen}
                onClose={() => setIsExportOpen(false)}
                filters={filters}
                count={results.length}
                selectedProductIds={selectedProducts}
            />
            <ImportModal isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} onImport={handleImport} />

            {/* Header */}
            <div className="h-20 border-b border-stone-800 flex items-center justify-between px-8 bg-stone-900/50 backdrop-blur-sm shrink-0">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-500/10 rounded-xl">
                        <List className="text-blue-400" size={24} />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-white">Product List</h2>
                        <p className="text-sm text-stone-400">{results.length} products found</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setIsImportOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 hover:bg-stone-800 rounded-lg text-stone-400 hover:text-white transition-colors border border-stone-800"
                        title="Import Data"
                    >
                        <Upload size={18} />
                        <span>Import</span>
                    </button>
                    <button
                        onClick={() => setIsExportOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-stone-900 hover:bg-stone-800 rounded-lg text-stone-400 hover:text-white transition-colors border border-stone-800 relative group"
                        title="Export Data"
                    >
                        <Download size={18} />
                        <span>Export</span>
                        {selectedProducts.length > 0 && (
                            <span className="absolute -top-2 -right-2 w-5 h-5 bg-blue-500 text-white text-xs font-bold flex items-center justify-center rounded-full">
                                {selectedProducts.length}
                            </span>
                        )}
                    </button>
                    <div className="h-8 w-px bg-stone-800 mx-2"></div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-red-900/20 hover:text-red-400 rounded-lg text-stone-500 transition-colors"
                        title="Close"
                    >
                        <X size={24} />
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Search & Filter Bar */}
                <div className="p-6 border-b border-stone-800 bg-stone-900/30 shrink-0">
                    <div className="flex gap-4 mb-4">
                        <div className="relative flex-1 max-w-2xl">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500" size={20} />
                            <input
                                type="text"
                                placeholder="Search products by SKU, Name, EAN..."
                                value={filters.q}
                                onChange={(e) => handleFilterChange('q', e.target.value)}
                                className="w-full bg-stone-950 border border-stone-800 rounded-xl pl-12 pr-4 py-3 text-base text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 outline-none transition-all placeholder-stone-600 shadow-sm"
                            />
                        </div>
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className={`px-4 py-2 rounded-xl border flex items-center gap-2 transition-all ${showAdvanced ? 'bg-blue-500/10 border-blue-500/50 text-blue-400' : 'bg-stone-900 border-stone-800 text-stone-400 hover:border-stone-700'}`}
                        >
                            <Filter size={18} />
                            <span>Filters</span>
                            {showAdvanced ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>

                        <div className="flex-1"></div>

                        <button
                            onClick={() => setFilters({
                                q: '', sku: '', model: '', ean: '', category: '', status: '', color: '',
                                trace_id: '', property_name: '', property_value: '', language_id: '', language_id_2: ''
                            })}
                            className="px-4 py-2 text-sm text-stone-500 hover:text-white transition-colors"
                        >
                            Clear Filters
                        </button>
                    </div>

                    {/* Advanced Filters Panel */}
                    <div className={`transition-all duration-300 overflow-hidden ${showAdvanced ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}>
                        <div className="bg-stone-950 border border-stone-800 rounded-xl p-6 shadow-inner">
                            <div className="grid grid-cols-4 gap-6">
                                <div>
                                    <label className="text-xs font-bold text-stone-500 uppercase mb-2 block">SKU</label>
                                    <input
                                        type="text"
                                        value={filters.sku}
                                        onChange={(e) => handleFilterChange('sku', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500/50 outline-none"
                                        placeholder="Filter by SKU..."
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-stone-500 uppercase mb-2 block">Model</label>
                                    <input
                                        type="text"
                                        value={filters.model}
                                        onChange={(e) => handleFilterChange('model', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500/50 outline-none"
                                        placeholder="Filter by Model..."
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-stone-500 uppercase mb-2 block">Category</label>
                                    <select
                                        value={filters.category}
                                        onChange={(e) => handleFilterChange('category', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500/50 outline-none"
                                    >
                                        <option value="">All Categories</option>
                                        {categories.map(c => (
                                            <option key={c.id || c} value={c.name || c}>{c.name || c}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-stone-500 uppercase mb-2 block">Status</label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500/50 outline-none"
                                    >
                                        <option value="">All Statuses</option>
                                        {statuses.map(s => (
                                            <option key={s.id || s} value={s.name || s}>{s.name || s}</option>
                                        ))}
                                    </select>
                                </div>
                                {/* Add more filters here if needed, or keep them in a second row */}
                            </div>

                            {/* More advanced filters can go here in a second row or collapsible section within the panel */}
                            <div className="mt-6 pt-6 border-t border-stone-800 grid grid-cols-3 gap-6">
                                <div>
                                    <label className="text-xs font-bold text-stone-500 uppercase mb-2 block">Property Search</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={filters.property_name}
                                            onChange={(e) => handleFilterChange('property_name', e.target.value)}
                                            className="flex-1 bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500/50 outline-none"
                                            placeholder="Property Name"
                                            list="property-names"
                                        />
                                        <input
                                            type="text"
                                            value={filters.property_value}
                                            onChange={(e) => handleFilterChange('property_value', e.target.value)}
                                            className="flex-1 bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500/50 outline-none"
                                            placeholder="Value"
                                        />
                                    </div>
                                    <datalist id="property-names">
                                        {availableProperties.map(p => <option key={p.id} value={p.name} />)}
                                    </datalist>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-stone-500 uppercase mb-2 block">Language Availability</label>
                                    <select
                                        value={filters.language_id}
                                        onChange={(e) => handleFilterChange('language_id', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500/50 outline-none"
                                    >
                                        <option value="">Select Language...</option>
                                        {availableLanguages.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-stone-500 uppercase mb-2 block">Trace ID (IMEI/Serial)</label>
                                    <input
                                        type="text"
                                        value={filters.trace_id}
                                        onChange={(e) => handleFilterChange('trace_id', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500/50 outline-none"
                                        placeholder="Search IMEI/Serial..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* List Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="bg-stone-900/50 border border-stone-800 rounded-xl overflow-hidden shadow-2xl">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-stone-950 text-xs font-bold text-stone-500 uppercase tracking-wider sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 border-b border-stone-800 w-16 text-center">
                                        <input
                                            type="checkbox"
                                            checked={results.length > 0 && selectedProducts.length === results.length}
                                            onChange={toggleSelectAll}
                                            className="w-4 h-4 rounded border-stone-600 bg-stone-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-stone-900 cursor-pointer"
                                        />
                                    </th>
                                    <th className="px-6 py-4 border-b border-stone-800 text-stone-500 w-16">ID</th>
                                    <th className="px-6 py-4 border-b border-stone-800">Product</th>
                                    <th className="px-6 py-4 border-b border-stone-800">Category</th>
                                    <th className="px-6 py-4 border-b border-stone-800">EAN</th>
                                    <th className="px-6 py-4 border-b border-stone-800">Color</th>
                                    <th className="px-6 py-4 border-b border-stone-800 text-right">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-800/50">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-stone-500">
                                            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                                            Loading products...
                                        </td>
                                    </tr>
                                ) : results.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-stone-500">
                                            No products found matching your criteria.
                                        </td>
                                    </tr>
                                ) : (
                                    results.map((product) => (
                                        <ProductRow
                                            key={product.id}
                                            product={product}
                                            isSelected={selectedProducts.includes(product.id)}
                                            onClick={handleProductSelect}
                                            onSelect={toggleSelectProduct}
                                            statuses={statuses}
                                            modifiedStatus={modifiedStatuses[product.id]}
                                            onStatusChange={handleStatusChange}
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {/* Floating Save Button */}
            {Object.keys(modifiedStatuses).length > 0 && (
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
                    <div className="bg-stone-900 border border-blue-500/30 shadow-2xl shadow-blue-900/20 rounded-xl p-2 flex items-center gap-4 pr-4">
                        <div className="px-3 py-1 bg-blue-500/10 rounded text-blue-400 text-sm font-bold">
                            {Object.keys(modifiedStatuses).length} modified
                        </div>
                        <span className="text-stone-400 text-sm">Unsaved changes</span>
                        <div className="h-6 w-px bg-stone-800"></div>
                        <button
                            onClick={() => setModifiedStatuses({})}
                            className="text-stone-500 hover:text-white text-sm transition-colors"
                        >
                            Cancel
                        </button>
                        <PrimaryButton
                            onClick={handleBulkSave}
                            disabled={isSaving}
                            className="ml-2"
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </PrimaryButton>
                    </div>
                </div>
            )}
        </div>
    );
};

const ProductRow = React.memo(({ product, isSelected, onClick, onSelect, statuses, modifiedStatus, onStatusChange }) => {
    // Use modified status if available, else product status
    const currentStatusName = modifiedStatus || product.status;
    const statusObj = statuses.find(s => s.name === currentStatusName);
    const color = statusObj?.hex_code || '#78716c';

    return (
        <tr
            onClick={() => onClick(product.sku)}
            className={`group hover:bg-stone-800/50 transition-colors cursor-pointer ${modifiedStatus ? 'bg-blue-900/10' : ''}`}
        >
            <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelect(product.id)}
                    className="w-4 h-4 rounded border-stone-600 bg-stone-800 text-blue-500 focus:ring-blue-500 focus:ring-offset-stone-900 cursor-pointer"
                />
            </td>
            <td className="px-6 py-4 text-stone-500 font-mono text-xs">
                {product.id}
            </td>
            <td className="px-6 py-4">
                <div className="flex items-center gap-4">
                    {product.image_url ? (
                        <img src={product.image_url.startsWith('/') ? product.image_url : `/${product.image_url}`} alt="" className="w-10 h-10 rounded bg-stone-800 object-cover" />
                    ) : (
                        <div className="w-10 h-10 rounded bg-stone-800 flex items-center justify-center text-stone-600">
                            <Smartphone size={20} />
                        </div>
                    )}
                    <div>
                        <div className="font-bold text-stone-200 group-hover:text-white transition-colors text-lg">{product.model_name}</div>
                        <div className="text-sm text-stone-500 font-mono flex items-center gap-2">
                            <Hash size={12} /> {product.sku}
                        </div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4">
                <span className="text-sm px-3 py-1 rounded-full bg-stone-800 text-stone-300 border border-stone-700">
                    {product.category}
                </span>
            </td>
            <td className="px-6 py-4 text-stone-400 font-mono text-sm">
                {product.ean || '-'}
            </td>
            <td className="px-6 py-4 text-stone-400 text-sm">
                {product.color || '-'}
            </td>
            <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                <div className="relative inline-block group/status">
                    <select
                        value={currentStatusName}
                        onChange={(e) => onStatusChange(product.id, e.target.value)}
                        className="appearance-none cursor-pointer pl-3 pr-8 py-1 rounded-full border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all text-white bg-transparent"
                        style={{
                            backgroundColor: `${color}20`,
                            borderColor: `${color}40`,
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                        }}
                    >
                        {statuses.map(s => (
                            <option key={s.id || s} value={s.name || s} className="bg-stone-900 text-stone-200">
                                {s.name || s}
                            </option>
                        ))}
                    </select>
                    {/* Status Pill Style Overlay (optional, relying on select styling for now) */}
                </div>
            </td>
        </tr>
    );
});
